// Raw-row → domain mappers for the spend flow. The backend returns spend records
// as raw DB rows (lowercase columns); these turn them into the client domain
// shapes. Shared so the Inbox list and the evidence detail screen normalise the
// same way — and so an ask fetched by id resolves to the exact shape (and id) the
// Inbox linked with.
import type { SpendRequest, SpendItem, EvidenceItem, AttachedDocument } from "./types";

// Kept single-argument: callers pass it straight to `.map()`, where a second
// parameter would silently bind to the index. The detail-only extras (documents,
// per-item evidence) are assembled by normalizeSpendRequestDetail below.
export function normalizeSpendRequest(row: Record<string, unknown>): SpendRequest {
  return {
    id: String(row.id),
    reporterId: String(row.requesterid ?? ""),
    organisationId: String(row.requesterorganisationid ?? row.organisationid ?? ""),
    projectId: String(row.projectid ?? ""),
    subProjectId: row.subprojectid == null ? undefined : String(row.subprojectid),
    milestoneId: row.milestoneid == null ? undefined : String(row.milestoneid),
    title: String(row.title ?? ""),
    amount: Number(row.amount ?? 0),
    currency: String(row.currency ?? "UGX") as SpendRequest["currency"],
    supplier: row.targetsupplier == null ? undefined : String(row.targetsupplier),
    purpose: String(row.purpose ?? ""),
    documents: [],
    state: String(row.state ?? "proposed") as SpendRequest["state"],
    submittedAt: String(row.createdat ?? new Date().toISOString()),
    // Independent verification (the assurance ladder's top rung). These were
    // dropped on the way through, so an ask oversight had marked verified read
    // back as self-reported on the next load and the badge silently reverted.
    verifiedById: row.verifiedbyuserid == null ? undefined : String(row.verifiedbyuserid),
    verifiedAt: row.verifiedat == null ? undefined : String(row.verifiedat),
    verificationNote: row.verificationnote == null ? undefined : String(row.verificationnote),
  };
}

// spend_request_document holds the supporting files attached when the ask was
// raised (§6.2 quotes, contract excerpts, supplier estimates), as opposed to
// evidence_package, which is proof captured after the money went out.
export function normalizeSpendDocument(row: Record<string, unknown>): AttachedDocument {
  return {
    id: String(row.id),
    filename: String(row.filename ?? "Attachment"),
    kind: documentKind(row.filetype),
    sizeBytes: Number(row.filebytes ?? 0),
    uploadedAt: String(row.createdat ?? new Date().toISOString()),
    uploadedById: String(row.uploadedbyuserid ?? ""),
    url: row.fileurl == null ? undefined : String(row.fileurl),
  };
}

// filetype uses evidenceFileStore.js's vocabulary ("photo" | "pdf" | "document");
// AttachedDocument's `kind` drives whether the row renders an inline preview.
function documentKind(filetype: unknown): AttachedDocument["kind"] {
  if (filetype === "photo") return "image";
  if (filetype === "pdf") return "pdf";
  return "doc";
}

// spend_item has no `description` column — the free-text `note` (falling back to the
// supplier) is what the reporter typed as the line's description, so use that.
export function normalizeSpendItem(row: Record<string, unknown>): SpendItem {
  return {
    id: String(row.id),
    spendRequestId: String(row.spendrequestid ?? ""),
    description: String(row.note ?? row.supplier ?? ""),
    amount: Number(row.amount ?? 0),
    currency: "UGX",
    supplier: row.supplier == null ? undefined : String(row.supplier),
    note: row.note == null ? undefined : String(row.note),
    evidence: [],
    committedAt: String(row.createdat ?? new Date().toISOString()),
  };
}

// Map what evidence_package holds; the UI falls back to the type label when
// there is no filename.
export function normalizeEvidence(row: Record<string, unknown>): EvidenceItem {
  // filetype is evidence_package's own vocabulary ("photo"|"pdf"|"document",
  // see evidenceFileStore.js's filetypeFor) - "pdf" has no direct match in
  // EvidenceItem's narrower type, so it folds into "document".
  const type = row.filetype === "photo" ? "photo" : "document";
  // Capture location, when the device recorded one. Stored as numerics, so they
  // can arrive as strings over JSON.
  const lat = row.geolatitude == null ? null : Number(row.geolatitude);
  const lng = row.geolongitude == null ? null : Number(row.geolongitude);
  const geo =
    lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)
      ? { lat, lng }
      : undefined;
  return {
    id: String(row.id),
    type,
    url: row.fileurl == null ? "" : String(row.fileurl),
    filename: row.filename == null ? undefined : String(row.filename),
    note: row.notes == null ? undefined : String(row.notes),
    geo,
    capturedAt: String(row.geocapturedat ?? row.createdat ?? new Date().toISOString()),
    submittedById: String(row.submittedbyuserid ?? ""),
    linkedAskId: String(row.spendrequestid ?? ""),
    linkedItemId: row.spenditemid == null ? undefined : String(row.spenditemid),
  };
}

// Render an evidence package through the same attachment row the ask's own
// documents use, so field proof previews and downloads identically (§6). The
// evidence-only metadata (note, submitter, capture time, geo) is rendered
// around the row by whoever shows it.
export function evidenceAsDocument(e: EvidenceItem): AttachedDocument {
  return {
    id: e.id,
    filename: e.filename ?? (e.type === "photo" ? "Photo" : "Document"),
    kind: e.type === "photo" ? "image" : "doc",
    sizeBytes: 0,
    uploadedAt: e.capturedAt,
    uploadedById: e.submittedById,
    url: e.url || undefined,
  };
}

/**
 * Assemble one ask's full detail from GET /approval/spend-request/:id.
 *
 * The row-level mappers above stay single-argument so they can be passed to
 * `.map()`; this is where the cross-referencing lives — supporting documents
 * onto the ask, and each evidence package onto the line item it was captured
 * against (§6.5's recommended per-item evidence). Evidence with no item stays
 * in the returned `evidence` list as a bundle submission against the ask.
 */
export function normalizeSpendRequestDetail(detail: {
  spendRequest: Record<string, unknown>;
  items: Record<string, unknown>[];
  evidence: Record<string, unknown>[];
  documents?: Record<string, unknown>[];
}): { ask: SpendRequest; items: SpendItem[]; evidence: EvidenceItem[] } {
  const evidence = detail.evidence.map(normalizeEvidence);
  return {
    ask: {
      ...normalizeSpendRequest(detail.spendRequest),
      documents: (detail.documents ?? []).map(normalizeSpendDocument),
    },
    items: detail.items.map((row) => {
      const item = normalizeSpendItem(row);
      return {
        ...item,
        evidence: evidence.filter((e) => e.linkedItemId === item.id).map(evidenceAsDocument),
        evidenceIds: evidence.filter((e) => e.linkedItemId === item.id).map((e) => e.id),
      };
    }),
    evidence,
  };
}
